import React from "react";
import { publicLink } from "@/lib/landerDomain";
import { toast } from "sonner";
import { X, ChartLineUp, ArrowSquareOut, ArrowClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-75.11 — Pitch-to-Live conversion analytics modal.
 *
 * Renders the response from `GET /api/admin/custom-landing-pages/pitch-analytics`
 * in two panels:
 *   1. Top samples — which templates pull the most pitches + leads.
 *   2. Per-pitch table — every live pitched page with its first-lead
 *      time and time_to_first_lead_hours.
 *
 * Super_admin only (endpoint enforces).
 */
export default function PitchAnalyticsPanel({ onClose }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pitch-analytics");
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analytics load failed");
      setData({ total_pitches: 0, total_leads_from_pitches: 0,
                pitched_pages: [], samples: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const fmtHours = (h) => {
    if (h == null) return "—";
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center overflow-y-auto p-4"
      data-testid="pitch-analytics-modal"
    >
      <div className="bg-white border-2 border-black shadow-[8px_8px_0_#000] w-full max-w-6xl my-8">
        <div className="flex items-center justify-between border-b-2 border-black p-4">
          <div>
            <div className="font-chivo font-black uppercase tracking-tight text-lg inline-flex items-center gap-2">
              <ChartLineUp size={16} weight="fill" className="text-emerald-700" />
              Pitch → Live analytics
            </div>
            <div className="label-mono">
              conversion + time-to-first-lead per pitched firm and sample
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="pitch-analytics-refresh"
              className="border border-black px-2 py-1.5 uppercase text-[10px] tracking-widest font-bold hover:bg-black hover:text-white disabled:opacity-40 inline-flex items-center gap-1"
              title="Reload counts"
            >
              <ArrowClockwise size={11} weight="bold" />
              {loading ? "…" : "Refresh"}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="pitch-analytics-close"
              className="border border-black p-2 hover:bg-black hover:text-white"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {loading && !data && (
            <div className="text-[12px] text-zinc-500">Loading…</div>
          )}
          {data && (
            <>
              {/* Top-line KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Live pitches"       value={data.total_pitches} testId="kpi-total-pitches" />
                <Kpi label="Leads from pitches" value={data.total_leads_from_pitches} testId="kpi-total-leads" />
                <Kpi label="Templates in use"   value={data.samples?.length || 0} testId="kpi-samples-in-use" />
                <Kpi label="Firms w/ 1st lead"
                     value={(data.pitched_pages || []).filter((p) => p.first_lead_at).length}
                     testId="kpi-firms-first-lead" />
              </div>

              {/* Top samples */}
              <div>
                <div className="font-chivo font-black uppercase tracking-tight text-sm mb-2">
                  Top-performing sample templates
                </div>
                {(!data.samples || data.samples.length === 0) ? (
                  <div className="text-[11px] text-zinc-500 border border-zinc-300 p-3 bg-zinc-50">
                    No pitched pages yet — pitch a sample to see analytics land here.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="pitch-analytics-samples-table">
                      <thead>
                        <tr className="border-b-2 border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                          <th className="text-left py-2 px-3">Sample</th>
                          <th className="text-right py-2 px-3">Pitches</th>
                          <th className="text-right py-2 px-3">Firms w/ 1st lead</th>
                          <th className="text-right py-2 px-3">Cumulative leads</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.samples.map((s) => (
                          <tr key={s.sample_id} data-testid={`pitch-analytics-sample-row-${s.sample_slug}`}
                              className="border-b border-zinc-200 hover:bg-zinc-50">
                            <td className="py-2 px-3 font-mono">
                              <a href={`/lp/${s.sample_slug}?preview=1`} target="_blank" rel="noreferrer"
                                 className="underline hover:text-emerald-700">
                                /lp/{s.sample_slug}
                              </a>
                            </td>
                            <td className="py-2 px-3 text-right font-bold">{s.pitches_count}</td>
                            <td className="py-2 px-3 text-right">{s.pages_with_first_lead}</td>
                            <td className="py-2 px-3 text-right">{s.leads_total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Per-pitch table */}
              <div>
                <div className="font-chivo font-black uppercase tracking-tight text-sm mb-2">
                  Every live pitched page
                </div>
                {(!data.pitched_pages || data.pitched_pages.length === 0) ? (
                  <div className="text-[11px] text-zinc-500 border border-zinc-300 p-3 bg-zinc-50">
                    None yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="pitch-analytics-pitches-table">
                      <thead>
                        <tr className="border-b-2 border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                          <th className="text-left  py-2 px-3">Firm</th>
                          <th className="text-left  py-2 px-3">Slug</th>
                          <th className="text-left  py-2 px-3">From sample</th>
                          <th className="text-left  py-2 px-3">Pitched at</th>
                          <th className="text-left  py-2 px-3">1st lead at</th>
                          <th className="text-right py-2 px-3">Time-to-1st</th>
                          <th className="text-right py-2 px-3">Leads</th>
                          <th className="text-right py-2 px-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.pitched_pages.map((p) => (
                          <tr key={p.page_id} data-testid={`pitch-analytics-pitch-row-${p.slug}`}
                              className="border-b border-zinc-200 hover:bg-zinc-50">
                            <td className="py-2 px-3 font-bold">{p.firm_name}</td>
                            <td className="py-2 px-3 font-mono">/lp/{p.slug}</td>
                            <td className="py-2 px-3 font-mono text-[10.5px] text-zinc-600">
                              {p.source_sample_slug || "—"}
                            </td>
                            <td className="py-2 px-3 text-[10.5px]">{fmtDate(p.pitched_at)}</td>
                            <td className="py-2 px-3 text-[10.5px]">{fmtDate(p.first_lead_at)}</td>
                            <td className="py-2 px-3 text-right font-bold">{fmtHours(p.time_to_first_lead_hours)}</td>
                            <td className="py-2 px-3 text-right">{p.leads_total}</td>
                            <td className="py-2 px-3 text-right">
                              <a href={publicLink(`/lp/${p.slug}`)} target="_blank" rel="noreferrer"
                                 className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
                                 data-testid={`pitch-analytics-open-${p.slug}`}>
                                <ArrowSquareOut size={10} weight="bold" /> Open
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, testId }) {
  return (
    <div className="border-2 border-black p-3 bg-white" data-testid={testId}>
      <div className="label-mono">{label}</div>
      <div className="font-chivo font-black text-3xl leading-none mt-1"
           data-testid={`${testId}-value`}>{value ?? 0}</div>
    </div>
  );
}
